import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { Eyebrow, GoldRule, Reveal } from "./Reveal";

const QA = [
  {
    q: "À partir de combien de pièces peut-on commander des peignoirs brodés ?",
    a: "La broderie personnalisée est proposée à partir de 5 pièces, pour permettre aux salons de coiffure de créer des tenues à leur image.",
  },
  {
    q: "Où sont fabriqués les peignoirs et kimonos Tarantina ?",
    a: "Nos créations sont confectionnées dans la Drôme, en France, avec une attention particulière portée à la qualité des matières et aux finitions.",
  },
  {
    q: "Quels sont les délais de confection et de livraison ?",
    a: "Les délais varient selon les modèles, les quantités et la personnalisation souhaitée. Contactez-nous pour obtenir une estimation adaptée à votre commande.",
  },
  {
    q: "Comment entretenir les peignoirs en tissu Burlington ?",
    a: "Le tissu Burlington est facile d'entretien. Il est recommandé de suivre les indications de lavage figurant sur l'étiquette du produit. Quelques précautions d'usage :",
    points: [
      "Vos serviettes risquent de déteindre sur vos textiles, même si elles ont été lavées plusieurs fois.",
      "Le frottement des serviettes sur le polyester augmente le phénomène d'électricité statique.",
      "Le poids des serviettes écrase le polyester et froisse les textiles.",
      "Les fibres du coton viennent se fixer dans le polyester.",
      "L'assouplissant protège les fibres de vos textiles et limite le phénomène d'électricité statique — nous recommandons d'en utiliser régulièrement.",
      "Suspendez vos cintres sur un support en contact direct avec un mur ou le sol pour décharger l'électricité statique.",
    ],
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto"
    >
      <Reveal>
        <Eyebrow>Questions fréquentes</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight max-w-2xl">
          Tout savoir sur nos{" "}
          <em className="italic text-[#D4AF37]">
            kimonos et peignoirs de coiffure
          </em>
          .
        </h2>
        <GoldRule className="mt-8" />
      </Reveal>

      <div className="mt-14 max-w-3xl space-y-4">
        {QA.map((item, i) => (
          <Reveal key={item.q} delay={i * 0.08}>
            <div
              data-testid={`faq-item-${i}`}
              className="overflow-hidden rounded-2xl border border-[#E2DBCB]/10 bg-[#141619] transition-colors duration-300 hover:border-[#D4AF37]/30"
            >
              <button
                data-testid={`faq-question-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="flex w-full items-center justify-between gap-6 px-6 py-5 sm:px-8 text-left"
              >
                <span className="font-serif text-lg sm:text-xl font-medium text-[#F7F5F0]">
                  {item.q}
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37]">
                  {open === i ? <Minus size={14} /> : <Plus size={14} />}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <p
                      data-testid={`faq-answer-${i}`}
                      className={`px-6 sm:px-8 text-sm leading-relaxed text-[#B0ABA0] ${
                        item.points ? "pb-4" : "pb-6"
                      }`}
                    >
                      {item.a}
                    </p>
                    {item.points && (
                      <ul className="space-y-2.5 px-6 pb-6 sm:px-8">
                        {item.points.map((pt) => (
                          <li
                            key={pt}
                            className="flex items-start gap-3 text-sm leading-relaxed text-[#B0ABA0]"
                          >
                            <span className="mt-2 h-1 w-1 rotate-45 shrink-0 bg-[#D4AF37]" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
